import type { TableColumn } from 'uikit';

import type { ColDef, ColGroupDef, ColumnState } from 'ag-grid-community';

interface IGetPinnedColumnsProps<T> {
    columns: (ColDef<T> | ColGroupDef<T>)[];
    pinnedItems: Record<string, boolean>;
}

interface IGetPinnedFromTableState<T> {
    columns: (ColDef<T> | ColGroupDef<T>)[];
    tableStateWithOnlyPinned: ColumnState[];
    parentColumnName?: string;
}

interface IConfirmSettings<TData> {
    settingColumns: (ColDef<TData> | ColGroupDef<TData>)[];
    incomingColumns: { pinnedColumNames: string[] } | { [key: string]: string };
    setSelection: (...args: any[]) => void;
    setPinned: (...args: any[]) => void;
}

export const getColumnId = (
    column: Partial<TableColumn<any>> & {
        headerName?: string | null;
        hiddenTitle?: string | null;
        colId?: string | null;
    },
) => {
    if (column.colId) {
        return column.colId;
    }

    if (column.headerName) {
        return column.headerName;
    }

    if (column.hiddenTitle) {
        return column.hiddenTitle;
    }

    if (column.title && typeof column.title === 'string') {
        return column.title;
    }

    if (column.accessor && typeof column.accessor === 'string') {
        return column.accessor;
    }

    return '';
};

const updatePinnedProperty = <T>(
    column: ColDef<T> | ColGroupDef<T>,
    pinned: ColDef['pinned'],
): ColDef<T> | ColGroupDef<T> => {
    if ('children' in column) {
        return {
            ...column,
            children: column.children.map((child) => updatePinnedProperty(child, pinned)),
        };
    }

    return { ...column, pinned };
};

export const getPinnedFromTableState = <T>({
    tableStateWithOnlyPinned,
    columns,
    parentColumnName,
}: IGetPinnedFromTableState<T>): string[] =>
    columns.reduce((acc: string[], column) => {
        const columnName = parentColumnName ?? getColumnId(column);

        // Check if the column itself is pinned
        const isColumnPinned =
            ('field' in column || 'colId' in column) &&
            tableStateWithOnlyPinned?.some((state) => state.colId === column.colId || state.colId === column.field);

        if (isColumnPinned && !acc.includes(columnName)) {
            acc.push(columnName);

            return acc;
        }

        // If the column has children, run recursively with the children
        if ('children' in column) {
            return acc.concat(
                getPinnedFromTableState({
                    tableStateWithOnlyPinned,
                    columns: column.children,
                    parentColumnName: columnName,
                }),
            );
        }

        return acc;
    }, []);

export const getColumnsWithPinnedProp = <T>({ columns, pinnedItems }: IGetPinnedColumnsProps<T>) =>
    columns.map((column) => {
        const isPinnedByDefault = 'pinned' in column && column.pinned;
        const isPinned = isPinnedByDefault || pinnedItems[getColumnId(column)];

        return updatePinnedProperty(column, isPinned);
    });

export const onTableSettingsConfirm = <TData = unknown>({
    settingColumns,
    incomingColumns,
    setSelection,
    setPinned,
}: IConfirmSettings<TData>) => {
    const { pinnedColumNames, ...visible } = incomingColumns;

    const clearedColumns = settingColumns.reduce((acc: Record<string, false>, column) => {
        acc[getColumnId(column)] = false;

        return acc;
    }, {});

    const pinnedColumns =
        Array.isArray(pinnedColumNames) &&
        pinnedColumNames.reduce((acc: Record<string, true>, columnName: string) => {
            acc[columnName] = true;

            return acc;
        }, {});

    setSelection({ ...clearedColumns, ...visible });
    setPinned({ ...clearedColumns, ...pinnedColumns });
};
